<template>
  <div class="container">
    <div class="fortune-crystal">
      <div class="starry-bg"></div>

      <div class="control-panel">
        <select v-model="selectedSign" class="zodiac-select">
          <option
            v-for="sign in zodiacSigns"
            :key="sign.value"
            :value="sign.value"
          >
            {{ sign.label }} {{ getSymbol(sign.value) }}
          </option>
        </select>

        <select v-model="selectedTime" class="time-select">
          <option
            v-for="time in timeOptions"
            :key="time.value"
            :value="time.value"
          >
            {{ time.label }}
          </option>
        </select>
      </div>

      <transition name="crystal-glow">
        <div v-if="fortuneData" class="fortune-card">
          <div class="crystal-header">
            <h2>{{ fortuneData.data.title }} {{ zodiacSymbol }}</h2>
            <div class="meta-info">
              <span>📆 {{ fortuneData.data.time }}</span>
              <span>
                🎨
                <span
                  class="color-block"
                  :style="{ backgroundColor: fortuneData.data.luckycolor }"
                ></span>
              </span>
              <span>🔢 {{ fortuneData.data.luckynumber }}</span>
              <span>👑 {{ fortuneData.data.luckyconstellation }}</span>
            </div>
          </div>

          <div class="main-content">
            <div class="category-tabs">
              <button
                v-for="(label, key) in keyMap"
                :key="key"
                @click="activeTab = key"
                :class="{ active: activeTab === key }"
              >
                {{ label }}
              </button>
            </div>

            <div class="dynamic-content">
              <div v-show="activeTab === 'all'" class="overview-content">
                <div class="radar-container">
                  <canvas ref="radarCanvas"></canvas>
                </div>
                <div class="guide-info">
                  <div class="guide-item">
                    <h3>今日宜</h3>
                    <p>{{ fortuneData.data.todo.yi }}</p>
                  </div>
                  <div class="guide-item">
                    <h3>今日忌</h3>
                    <p>{{ fortuneData.data.todo.ji }}</p>
                  </div>
                </div>
              </div>

              <div v-show="activeTab !== 'all'" class="detail-content">
                <div class="star-rating">
                  <span
                    v-for="n in 5"
                    :key="n"
                    :class="['star', { active: n <= currentRating }]"
                  ></span>
                  <span class="rating-text">{{ currentRating }} 星运势</span>
                </div>
                <p class="description">
                  {{ fortuneData.data.fortunetext[activeTab] }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <div v-if="error" class="crystal-error">💥 {{ error }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import axios from "axios";
import Chart from "chart.js/auto";

const apiUrl = "https://api.vvhan.com/api/horoscope";

const zodiacSigns = [
  { label: "白羊座", value: "aries" },
  { label: "金牛座", value: "taurus" },
  { label: "双子座", value: "gemini" },
  { label: "巨蟹座", value: "cancer" },
  { label: "狮子座", value: "leo" },
  { label: "处女座", value: "virgo" },
  { label: "天秤座", value: "libra" },
  { label: "天蝎座", value: "scorpio" },
  { label: "射手座", value: "sagittarius" },
  { label: "摩羯座", value: "capricorn" },
  { label: "水瓶座", value: "aquarius" },
  { label: "双鱼座", value: "pisces" },
];

const timeOptions = [
  { label: "今日运势", value: "today" },
  { label: "明日运势", value: "nextday" },
  { label: "本周运势", value: "week" },
  { label: "本月运势", value: "month" },
];

const keyMap = {
  all: "整体运势",
  love: "爱情运势",
  work: "事业学业",
  money: "财富运势",
  health: "健康发展",
};

const selectedSign = ref("cancer");
const selectedTime = ref("today");
const activeTab = ref("all");
const fortuneData = ref(null);
const loading = ref(false);
const error = ref(null);
const radarCanvas = ref(null);
let radarChartInstance = null;

const zodiacSymbol = computed(() => {
  const symbols = [
    "♈",
    "♉",
    "♊",
    "♋",
    "♌",
    "♍",
    "♎",
    "♏",
    "♐",
    "♑",
    "♒",
    "♓",
  ];
  const index = zodiacSigns.findIndex((s) => s.value === selectedSign.value);
  return symbols[index] || "✨";
});

const currentRating = computed(() => {
  return fortuneData.value?.data.fortune[activeTab.value] || 0;
});

const getSymbol = (signValue) => {
  const index = zodiacSigns.findIndex((s) => s.value === signValue);
  return (
    ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"][
      index
    ] || "✨"
  );
};

const drawRadarChart = (data) => {
  if (radarChartInstance) {
    radarChartInstance.destroy();
    radarChartInstance = null;
  }
  if (radarCanvas.value) {
    radarCanvas.value.style.display = "block";
  }
  const ctx = radarCanvas.value.getContext("2d");
  radarChartInstance = new Chart(ctx, {
    type: "radar",
    data: {
      labels: Object.values(keyMap),
      datasets: [
        {
          label: "运势指数",
          data: Object.values(data.fortune),
          backgroundColor: "rgba(107, 114, 214, 0.5)",
          borderColor: "#6B72D6",
          pointBackgroundColor: "#FF6B6B",
          borderWidth: 1.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          max: 5,
          ticks: {
            stepSize: 1,
            color: "#CBD5E1",
            font: { size: 12 },
          },
          grid: { color: "rgba(255,255,255,0.15)" },
          pointLabels: { color: "#E2E8F0", font: { size: 13 } },
          angleLines: { color: "rgba(255,255,255,0.1)" },
        },
      },
      plugins: { legend: { display: false } },
    },
  });
};

const fetchHoroscope = async () => {
  try {
    loading.value = true;
    error.value = null;

    const { data } = await axios.get(apiUrl, {
      params: { type: selectedSign.value, time: selectedTime.value },
    });

    if (data.success) {
      fortuneData.value = data;
      nextTick(() => drawRadarChart(data.data));
    } else {
      throw new Error(data.message || "星座数据获取失败");
    }
  } catch (err) {
    error.value = err.response?.data?.message || "星象仪暂时无法连接喵～";
    fortuneData.value = null;
  } finally {
    loading.value = false;
  }
};

watch([selectedSign, selectedTime], fetchHoroscope);
watch(activeTab, (newVal) => {
  if (newVal === "all" && radarChartInstance) {
    nextTick(() => {
      radarChartInstance.update(); // 主动更新图表视图
    });
  }
});
onMounted(fetchHoroscope);
</script>

<style scoped>
.container {
  padding: 6px;
  display: flex;
  justify-content: center;
}

.fortune-crystal {
  width: 100%;
  max-width: 380px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

.control-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
}

.zodiac-select,
.time-select {
  padding: 6px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.3);
  color: white;
}

.fortune-card {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow-y: auto;
}

.crystal-header {
  position: sticky;
  top: 0;
  background: rgba(26, 26, 46, 0.9);
  padding: 5px 0;
  z-index: 1;
}

.crystal-header h2 {
  font-size: 16px;
  margin: 0;
  text-align: center;
  color: white;
}

.meta-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  font-size: 11px;
  margin: 6px 0;
}

.color-block {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-left: 3px;
  border-radius: 50%;
}

.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 8px 0;
  justify-content: center;
}

.category-tabs button {
  padding: 5px 8px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
  cursor: pointer;
}

.star-rating {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.star {
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234A5568"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>');
}

.star.active {
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23FFD700"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>');
}

.category-tabs button.active {
  background: linear-gradient(45deg, #6b72d6, #8e6cea);
}

.dynamic-content {
  min-height: 280px;
}

.radar-container {
  height: 150px;
  margin: 5px 0;
}

.guide-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 8px 0;
}

.guide-item {
  padding: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  font-size: 12px;
}

.guide-item h3 {
  margin: 0 0 3px 0;
  font-size: 13px;
  color: #ff6b6b;
}

.star-rating {
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 5px 0;
}

.star {
  width: 14px;
  height: 14px;
}

.description {
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0 0 0;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

::-webkit-scrollbar-thumb {
  background: rgba(107, 114, 214, 0.5);
  border-radius: 3px;
}

span {
  color: white;
}
/* 紧凑型调整 */
@media (max-width: 360px) {
  .guide-info {
    grid-template-columns: 1fr;
  }

  .radar-container {
    height: 130px;
  }

  .category-tabs button {
    padding: 4px 6px;
    font-size: 11px;
  }
}
</style>
